import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseInit';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { usePricing } from '../contexts/PricingContext';
import * as firestorePaths from '../utils/firestorePaths';
import {
  SUBSCRIPTION_STATUSES,
  getUsageStatus,
  getAgentUsageStatus,
  getSubscriptionStatusDisplay,
  getTrialDaysRemaining,
  isTrialExpired,
  formatCost,
  getEffectiveAgentLimit,
  isWhiteLabelEnabled
} from '../utils/usageTracking';
import {
  redirectToCheckout,
  openBillingPortal,
  purchaseEvalCredits,
  isStripeConfigured,
  formatPrice,
  calculateYearlySavings
} from '../utils/stripe';
import Button from './shared/Button';
import Card from './shared/Card';
import LoadingSpinner from './shared/LoadingSpinner';

/**
 * SubscriptionManager Component
 * 
 * Displays and manages subscription:
 * - Current plan and status
 * - Usage (evaluations, agents)
 * - Upgrade/downgrade options
 * - Add-on purchases (credits, agents, white labeling)
 * - Billing portal access
 */
const SubscriptionManager = ({ onClose }) => {
  // userTeam now auto-switches when admin views another team
  const { userTeam, userEmail, claims } = useAuth();
  const { showSuccess, showError, showWarning, showInfo } = useNotification();
  const { plans: PLANS, addons: ADDONS, getPaidPlans } = usePricing();
  
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [agentCount, setAgentCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [billingInterval, setBillingInterval] = useState('month');
  const [processingAction, setProcessingAction] = useState(null);
  
  const appId = firestorePaths.getArtifactProjectId();
  
  // Load subscription data with real-time updates
  useEffect(() => {
    if (!userTeam) {
      setLoading(false);
      return;
    }
    
    const teamPath = firestorePaths.getTeamPath(appId, userTeam);
    
    const unsubscribe = onSnapshot(
      doc(db, teamPath),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const teamData = docSnapshot.data();
          setSubscription(teamData.subscription || null);
          setUsage(teamData.subscription?.usage || null);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error loading subscription:', error);
        showError('Failed to load subscription data');
        setLoading(false);
      }
    );
    
    return () => unsubscribe();
  }, [userTeam, appId, showError]);
  
  // Load agent count
  useEffect(() => {
    async function loadAgentCount() {
      if (!userTeam) return;
      
      try {
        const usersPath = firestorePaths.getUsersPath(appId, userTeam);
        const { getDocs, collection } = await import('firebase/firestore');
        const usersSnapshot = await getDocs(collection(db, usersPath));
        
        let count = 0;
        usersSnapshot.forEach(doc => {
          if (doc.data().role === 'basic') count++;
        });
        setAgentCount(count);
      } catch (error) {
        console.error('Error loading agent count:', error);
      }
    }
    
    loadAgentCount();
  }, [userTeam, appId]);
  
  // Handle plan upgrade
  const handleUpgrade = useCallback(async (planId) => {
    if (!isStripeConfigured()) {
      showWarning('Payment system is being configured. Please try again later.');
      return;
    }
    
    setProcessingAction(`upgrade-${planId}`);
    try {
      await redirectToCheckout(planId, billingInterval, userTeam, userEmail);
    } catch (error) {
      showError(error.message);
    } finally {
      setProcessingAction(null);
    }
  }, [billingInterval, userTeam, userEmail, showError, showWarning]);
  
  // Handle credit pack purchase
  const handlePurchaseCredits = useCallback(async (packId) => {
    if (!isStripeConfigured()) {
      showWarning('Payment system is being configured. Please try again later.');
      return;
    }
    
    setProcessingAction(`credits-${packId}`);
    try {
      await purchaseEvalCredits(packId, userTeam, userEmail);
    } catch (error) {
      showError(error.message);
    } finally {
      setProcessingAction(null);
    }
  }, [userTeam, userEmail, showError, showWarning]);
  
  // Handle billing portal
  const handleOpenBilling = useCallback(async () => {
    if (!subscription?.stripeCustomerId) {
      showInfo('No billing account found. Subscribe to a plan first.');
      return;
    }
    
    setProcessingAction('billing');
    try {
      await openBillingPortal(userTeam);
    } catch (error) {
      showError(error.message);
    } finally {
      setProcessingAction(null);
    }
  }, [userTeam, subscription, showError, showInfo]);
  
  if (loading) {
    return (
      <div className="p-4 flex justify-center">
        <LoadingSpinner size="large" message="Loading subscription..." />
      </div>
    );
  }

  if (!userTeam) {
    return (
      <div className="p-4 text-center">
        <p className="text-gray-600">Team information not available. Please try refreshing the page.</p>
      </div>
    );
  }
  
  if (!subscription) {
    return (
      <div className="p-4">
        <Card>
          <div className="p-4 text-center">
            <span className="text-4xl mb-4 block">📋</span>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No Subscription Found</h3>
            <p className="text-gray-600 mb-6">
              Choose a plan to get started with TreeGenie.
            </p>
            <Button variant="primary" onClick={() => handleUpgrade('starter')}>
              Start with Starter Plan
            </Button>
          </div>
        </Card>
      </div>
    );
  }
  
  const statusDisplay = getSubscriptionStatusDisplay(subscription);
  const isLifetime = subscription.status === SUBSCRIPTION_STATUSES.LIFETIME;
  const isTrialing = subscription.status === SUBSCRIPTION_STATUSES.TRIALING;
  const trialDaysLeft = isTrialing ? getTrialDaysRemaining(subscription.trialEndsAt) : 0;
  const trialExpired = isTrialing && isTrialExpired(subscription.trialEndsAt);
  
  const bonusCredits = subscription.addons?.bonusEvalCredits || 0;
  const evalUsage = usage ? getUsageStatus(
    subscription.plan,
    usage.evaluationsUsed || 0,
    bonusCredits,
    subscription.status
  ) : null;
  
  const agentUsage = getAgentUsageStatus(subscription, agentCount);
  const planConfig = PLANS[subscription.plan] || PLANS.starter;
  const whiteLabelEnabled = isWhiteLabelEnabled(subscription);
  
  // Get plans for comparison (from dynamic pricing context)
  const plans = ['starter', 'pro', 'pro_plus'].map(id => {
    const plan = PLANS[id];
    return {
      ...plan,
      yearlyMonthlyCost: plan.yearlyCost ? Math.round(plan.yearlyCost / 12) : plan.monthlyCost,
      yearlySavings: plan.monthlyCost ? plan.monthlyCost * 12 - plan.yearlyCost : 0
    };
  }).filter(Boolean);
  const currentPlanIndex = plans.findIndex(p => p.id === subscription.plan);
  
  return (
    <div className="space-y-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Subscription</h2>
          <p className="text-gray-600">Manage your plan and billing</p>
        </div>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        )}
      </div>
      
      {/* Trial Expired Warning */}
      {trialExpired && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-3">
            <span className="text-2xl">⏰</span>
            <div className="flex-1">
              <p className="font-semibold text-red-800">Your trial has expired</p>
              <p className="text-sm text-red-700">Choose a plan to continue using TreeGenie.</p>
            </div>
            <Button variant="primary" onClick={() => handleUpgrade('starter')}>
              Upgrade Now
            </Button>
          </div>
        </div>
      )}
      
      {/* Current Plan Card */}
      <Card>
        <div className="p-4">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-xl font-semibold text-gray-900">
                  {subscription.planName || planConfig.name}
                </h3>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-${statusDisplay.color}-100 text-${statusDisplay.color}-800`}>
                  {statusDisplay.icon} {statusDisplay.label}
                </span>
              </div>
              <p className="text-gray-600 text-sm">{planConfig.description}</p>
            </div>
            
            {/* Billing Actions */}
            {!isLifetime && !isTrialing && subscription.stripeCustomerId && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenBilling}
                disabled={processingAction === 'billing'}
              >
                {processingAction === 'billing' ? 'Opening...' : 'Manage Billing'}
              </Button>
            )}
          </div>
          
          {/* Usage Meters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {/* Evaluations */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Evaluations This Month</span>
                <span className="text-sm text-gray-500">
                  {usage?.evaluationsUsed || 0} / {evalUsage?.limit || planConfig.monthlyLimit}
                  {bonusCredits > 0 && <span className="text-green-600"> (+{bonusCredits})</span>}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    evalUsage?.percentUsed >= 90 
                      ? 'bg-red-500' 
                      : evalUsage?.percentUsed >= 70 
                        ? 'bg-yellow-500' 
                        : 'bg-blue-500'
                  }`}
                  style={{ width: `${Math.min(100, evalUsage?.percentUsed || 0)}%` }}
                />
              </div>
              {evalUsage?.warningLevel === 'blocked' && (
                <p className="text-xs text-red-600 mt-1">Limit reached - purchase more credits below</p>
              )}
            </div>
            
            {/* Agents */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Team Agents</span>
                <span className="text-sm text-gray-500">
                  {agentCount} / {agentUsage.isUnlimited ? '∞' : agentUsage.limit}
                </span>
              </div>
              {!agentUsage.isUnlimited && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      agentUsage.percentUsed >= 90 
                        ? 'bg-red-500' 
                        : agentUsage.percentUsed >= 70 
                          ? 'bg-yellow-500' 
                          : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(100, agentUsage.percentUsed || 0)}%` }}
                  />
                </div>
              )}
              {agentUsage.isUnlimited && (
                <p className="text-xs text-purple-600">Unlimited agents on your plan</p>
              )}
            </div>
          </div>
          
          {/* Features */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-2">Plan Features:</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {planConfig.features?.slice(0, 6).map((feature, i) => (
                <div key={i} className="flex items-center gap-1 text-sm text-gray-600">
                  <span className="text-green-500">✓</span>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
            {whiteLabelEnabled && (
              <div className="mt-2 flex items-center gap-1 text-sm text-purple-600">
                <span>🎨</span>
                <span>White labeling enabled</span>
              </div>
            )}
          </div>
        </div>
      </Card>
      
      {/* Credit Packs - Quick Purchase */}
      {!isLifetime && (
        <Card>
          <div className="p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Need More Evaluations?
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Purchase additional evaluation credits that never expire.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(ADDONS)
                .filter(([key]) => key.startsWith('eval_pack'))
                .map(([packId, pack]) => (
                  <button
                    key={packId}
                    onClick={() => handlePurchaseCredits(packId)}
                    disabled={processingAction === `credits-${packId}`}
                    className="p-3 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors text-left disabled:opacity-50"
                  >
                    <div className="text-lg font-semibold text-gray-800">
                      {pack.credits} credits
                    </div>
                    <div className="text-green-600 font-medium">
                      ${pack.price}
                    </div>
                    {pack.savings && (
                      <div className="text-xs text-gray-500">
                        Save {pack.savings}
                      </div>
                    )}
                    {pack.popular && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                        Popular
                      </span>
                    )}
                  </button>
                ))}
            </div>
          </div>
        </Card>
      )}
      
      {/* Plan Comparison */}
      {!isLifetime && (
        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {isTrialing ? 'Choose Your Plan' : 'Upgrade Your Plan'}
              </h3>
              
              {/* Billing Toggle */}
              <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => setBillingInterval('month')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    billingInterval === 'month' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingInterval('year')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    billingInterval === 'year' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Yearly
                  <span className="ml-1 text-green-600 text-xs">Save 1 month</span>
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {plans.map((plan, index) => {
                const isCurrentPlan = plan.id === subscription.plan;
                const isUpgrade = index > currentPlanIndex;
                const price = billingInterval === 'year' ? plan.yearlyCost : plan.monthlyCost;
                const monthlyEquiv = billingInterval === 'year' ? plan.yearlyMonthlyCost : plan.monthlyCost;
                
                return (
                  <div
                    key={plan.id}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      isCurrentPlan 
                        ? 'border-blue-500 bg-blue-50' 
                        : plan.popular 
                          ? 'border-blue-200 bg-white' 
                          : 'border-gray-200 bg-white'
                    }`}
                  >
                    {plan.popular && !isCurrentPlan && (
                      <span className="inline-block mb-2 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                        Most Popular
                      </span>
                    )}
                    {isCurrentPlan && (
                      <span className="inline-block mb-2 px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                        Current Plan
                      </span>
                    )}
                    
                    <h4 className="text-xl font-bold text-gray-900">{plan.name}</h4>
                    <p className="text-sm text-gray-600 mb-3">{plan.description}</p>
                    
                    <div className="mb-4">
                      <span className="text-3xl font-bold text-gray-900">
                        ${Math.round(monthlyEquiv)}
                      </span>
                      <span className="text-gray-600">/mo</span>
                      {billingInterval === 'year' && (
                        <span className="block text-sm text-green-600">
                          ${price}/year (save ${plan.yearlySavings})
                        </span>
                      )}
                    </div>
                    
                    <ul className="space-y-2 mb-4 text-sm">
                      <li className="flex items-center gap-2">
                        <span className="text-green-500">✓</span>
                        <span>{plan.agentLimit === null ? 'Unlimited' : plan.agentLimit} agents</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="text-green-500">✓</span>
                        <span>{plan.monthlyLimit} evaluations/month</span>
                      </li>
                      {plan.whiteLabelIncluded && (
                        <li className="flex items-center gap-2">
                          <span className="text-green-500">✓</span>
                          <span>White labeling included</span>
                        </li>
                      )}
                    </ul>
                    
                    <Button
                      variant={isCurrentPlan ? 'outline' : isUpgrade ? 'primary' : 'secondary'}
                      fullWidth
                      disabled={isCurrentPlan || processingAction === `upgrade-${plan.id}`}
                      onClick={() => handleUpgrade(plan.id)}
                    >
                      {processingAction === `upgrade-${plan.id}` 
                        ? 'Processing...' 
                        : isCurrentPlan 
                          ? 'Current Plan' 
                          : isUpgrade 
                            ? 'Upgrade' 
                            : 'Downgrade'}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}
      
      {/* Lifetime Badge */}
      {isLifetime && (
        <Card>
          <div className="p-4 text-center bg-gradient-to-r from-purple-50 to-indigo-50">
            <span className="text-5xl mb-4 block">👑</span>
            <h3 className="text-2xl font-bold text-purple-800 mb-2">
              Lifetime License
            </h3>
            <p className="text-purple-700 mb-4">
              Thank you for being an early adopter! You have lifetime access to Business features 
              with no monthly billing.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full text-purple-800">
              <span>✓</span>
              <span>Unlimited agents</span>
              <span className="mx-2">•</span>
              <span>✓</span>
              <span>300 evals/month</span>
              <span className="mx-2">•</span>
              <span>✓</span>
              <span>White labeling</span>
            </div>
          </div>
        </Card>
      )}
      
      {/* Add-ons Section */}
      {!isLifetime && !isTrialing && subscription.stripeSubscriptionId && (
        <Card>
          <div className="p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Add-ons</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* White Labeling Add-on */}
              {!whiteLabelEnabled && subscription.plan !== 'pro_plus' && (
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">🎨</span>
                    <div>
                      <h4 className="font-medium text-gray-900">White Labeling</h4>
                      <p className="text-sm text-gray-600">Custom branding for your team</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-lg font-semibold text-gray-900">
                      ${ADDONS.white_label.monthlyCost}/mo
                    </span>
                    <Button variant="outline" size="sm">
                      Add
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Extra Agents Add-on */}
              {!agentUsage.isUnlimited && (
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">👤</span>
                    <div>
                      <h4 className="font-medium text-gray-900">Additional Agent</h4>
                      <p className="text-sm text-gray-600">Add more team members</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-lg font-semibold text-gray-900">
                      ${ADDONS.extra_agent.monthlyCost}/mo each
                    </span>
                    <Button variant="outline" size="sm">
                      Add
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default SubscriptionManager;


